import {CONFIRM_ICON_EDITOR_OPTS, EmbeddedEditorAction, openDesignInEmbeddedEditor} from '@Libraries/embedded-editor';
import type {PageHashedId, PosterHashedId} from '@Components/social-media/publish-params.types';
import type {DesignSelection} from '@Components/social-media/social-media.types';
import {
  getAddableDesignCountUnderPlanLimit,
  getDesignSelectionsFromPageIdsMap,
  getDesignsPerPostPlanLimit,
  getFilteredDesigns,
  getWizardUploadDesignsModalOpts,
  isDesignCountWithinPlanLimit,
  WIZARD_UPLOAD_MEDIA_GA4_CALLBACKS,
} from '@Libraries/social-media-wizard-design-library';
import {MAX_UPLOADS_TO_DESIGNS} from '@Libraries/social-media-wizard-design-library.types';
import {getStartingURIForGallery} from '@Libraries/social-media-wizard-step-library';
import {getPostersPageIds} from '@Libraries/poster-library';
import type {CreateDesignFromMediaModalOpts} from '@Components/design-selector/design-selector.types';
import {DesignSelectorTab, LimitExceededAction} from '@Components/design-selector/design-selector.types';
import {openSocialMediaMultiPostingUpsellDialog} from '@Libraries/user.library';
import {openSocialMediaAddDesignModal} from '@Modals/social-media-add-design-modal/social-media-add-design-modal.library';
import {openDesignSelectorModal} from '@Modals/design-selector-modal/design-selector-modal.library';
import {disableNextButton, enableNextButton, updateDesignsToPublish} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {useDesignsToPublish} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {useAppDispatch} from '@/hooks';
import type {UseWizardDesignActionsParams, UseWizardDesignActionsResult} from './wizard-design-actions.types';

export function useWizardDesignActions({
  onDesignsStaged,
  onEmbeddedEditorOpened,
  setIsPreviewLoading,
  setIsLoadingEmbeddedEditor,
}: UseWizardDesignActionsParams): UseWizardDesignActionsResult {
  const dispatch = useAppDispatch();
  const designsToPublishTo = useDesignsToPublish();

  const getAddableDesignCount = (): number => {
    return getAddableDesignCountUnderPlanLimit(designsToPublishTo.length, MAX_UPLOADS_TO_DESIGNS);
  };

  const getUploadDesignsModalOpts = (): CreateDesignFromMediaModalOpts => {
    return getWizardUploadDesignsModalOpts({
      ...WIZARD_UPLOAD_MEDIA_GA4_CALLBACKS,
      maxSelectionNumber: getAddableDesignCount(),
      onDesignsCreated: (uploadedDesigns: DesignSelection[]): void => {
        dispatch(updateDesignsToPublish([...designsToPublishTo, ...getFilteredDesigns(uploadedDesigns, getAddableDesignCount())]));
        dispatch(enableNextButton());
      },
      onPosterCreationsStarted: (): void => {
        setIsPreviewLoading(true);
        dispatch(disableNextButton());
      },
    });
  };

  const addDesignModalMyUploadsSelectionCallback = (): void => {
    window.PMW.triggerCreateDesignFromUserUpload({
      ...getUploadDesignsModalOpts(),
      footerPrimaryCtaText: window.i18next.t('pmwjs_add_selected'),
      hideFooterSecondaryActions: true,
    });
  };

  const onPlanDesignLimitExceeded = (): LimitExceededAction => {
    openSocialMediaMultiPostingUpsellDialog();
    return LimitExceededAction.REJECT;
  };

  const addDesignModalMyDesignsSelectionCallback = (): void => {
    openDesignSelectorModal({
      designSelectorTabsToShow: [DesignSelectorTab.MY_DESIGNS, DesignSelectorTab.SHARED_WITH_ME],
      onAddSelected: (selectedDesigns: DesignSelection[]) => {
        setIsPreviewLoading(true);
        onDesignsStaged(selectedDesigns);
        dispatch(updateDesignsToPublish(selectedDesigns));
      },
      selectedDesigns: designsToPublishTo,
      maxSelection: getDesignsPerPostPlanLimit() ?? undefined,
      onLimitExceeded: onPlanDesignLimitExceeded,
    });
  };

  const addCreatedDesignPagesToPublishList = (newDesignId: string): void => {
    getPostersPageIds([newDesignId])
      .then((pageIdsMap: Record<PosterHashedId, PageHashedId[]>) => {
        if (!pageIdsMap[newDesignId]) {
          console.error('No page IDs found for poster:', newDesignId);
          return;
        }

        dispatch(updateDesignsToPublish([...designsToPublishTo, ...getFilteredDesigns(getDesignSelectionsFromPageIdsMap(pageIdsMap), getAddableDesignCount())]));
      })
      .catch((err: unknown) => {
        console.error('Error fetching poster page IDs:', err);
      });
  };

  const onAddDesignGalleryTemplateSelected = (templateSelected: DesignSelection): void => {
    setIsPreviewLoading(true);
    setIsLoadingEmbeddedEditor(true);

    openDesignInEmbeddedEditor(
      templateSelected.posterHashedId,
      onEmbeddedEditorOpened,
      addCreatedDesignPagesToPublishList,
      () => {
        setIsPreviewLoading(false);
      },
      EmbeddedEditorAction.COPY,
      undefined,
      CONFIRM_ICON_EDITOR_OPTS
    );
  };

  const addDesignModalCreateDesignSelectionCallback = (): void => {
    openDesignSelectorModal({
      designSelectorTabsToShow: [DesignSelectorTab.GALLERY_TEMPLATES],
      selectedDesigns: designsToPublishTo,
      designGalleryStartingURI: getStartingURIForGallery(),
      onDesignSelectionCallback: onAddDesignGalleryTemplateSelected,
      startDesignFromUploadBtnOpts: getUploadDesignsModalOpts(),
      designGalleryHeight: '88vh',
    });
  };

  const onAddDesignClick = (): void => {
    if (!isDesignCountWithinPlanLimit(designsToPublishTo.length + 1)) {
      openSocialMediaMultiPostingUpsellDialog();
      return;
    }

    openSocialMediaAddDesignModal({
      myUploadsSelectionCallback: addDesignModalMyUploadsSelectionCallback,
      myDesignsSelectionCallback: addDesignModalMyDesignsSelectionCallback,
      createDesignSelectionCallback: addDesignModalCreateDesignSelectionCallback,
    });
  };

  return {onAddDesignClick};
}
